<template>
   <QCard class="graph-card">
      <QCardSection class="bg-primary">
         <div class="text-h5 text-white">Variable Snapshot</div>
      </QCardSection>
      <div class="q-pa-md q-gutter-y-md flex flex-row" style="gap: 1em">
         <QBtnDropdown
            id="variable-selector-dropdown-1"
            color="primary"
            :label="activeVariableFilter.humanReadableName"
            outline
         >
            <QList>
               <template
                  v-for="variable in projectStore.variables"
                  :key="variable.id"
               >
                  <QItem
                     v-close-popup
                     clickable
                     @click="onVariableClick(variable.id)"
                  >
                     <QItemSection>
                        <QItemLabel>{{
                           variable.humanReadableName
                        }}</QItemLabel>
                     </QItemSection>
                  </QItem>
               </template>
            </QList>
         </QBtnDropdown>
         <QBtnDropdown
            v-if="enabled"
            id="variable-selector-dropdown-2"
            color="primary"
            :label="secondaryVariableFilter.humanReadableName"
            outline
         >
            <QList>
               <QItem
                  v-close-popup
                  clickable
                  @click="onSecondaryVariableClick(-1)"
               >
                  <QItemSection>
                     <QItemLabel>None</QItemLabel>
                  </QItemSection>
               </QItem>
               <template
                  v-for="variable in projectStore.variables.filter(
                     (x) => x.id != activeVariableFilter.id,
                  )"
                  :key="variable.id"
               >
                  <QItem
                     v-close-popup
                     clickable
                     @click="onSecondaryVariableClick(variable.id)"
                  >
                     <QItemSection>
                        <QItemLabel>{{
                           variable.humanReadableName
                        }}</QItemLabel>
                     </QItemSection>
                  </QItem>
               </template>
            </QList>
         </QBtnDropdown>
         <QToggle
            id="serving-conversion-toggle"
            v-model="conversionRateOrServingRate"
            size="sm"
            :val="conversionRateOrServingRate"
            label="Serving Rate or Conversion Rate"
         />
      </div>
      <QCardSection
         id="combinations-table-graph-container"
         class="col"
         horizontal
      >
         <QTable
            v-if="overallLoaded"
            id="combinations-table"
            class="col-8"
            dense
            :rows="rows"
            :columns="columns"
            row-key="variant"
            width="100%"
            :pagination="{
               page: 1,
               rowsPerPage: 10,
               descending: false,
            }"
            :hide-pagination="true"
            flat
         >
            <template #body-cell-variant="slotProps">
               <QTd :props="slotProps">
                  <span
                     v-if="slotProps.row.variantColor"
                     class="indicator"
                     :style="{ backgroundColor: slotProps.row.variantColor }"
                  ></span>
                  {{ slotProps.row.variant }}
               </QTd>
            </template>

            <template #header-cell-conversion-rate="slotProps">
               <QTh :props="slotProps"> Conversion Rate </QTh>
            </template>
            <template #no-data="{ icon, filter }">
               <div
                  id="no-data-container"
                  class="full-width row flex-center text-accent q-ma-lg"
               >
                  <QIcon
                     size="2em"
                     color="primary"
                     :name="filter ? 'filter_b_and_w' : icon"
                  />
                  <span id="no-data"
                     >No data match your query. Please change your filters and
                     try again.</span
                  >
               </div>
            </template>
         </QTable>
         <NoSelectionTable v-else-if="selectedVariableId === null" />
         <div v-else id="chart-skeleton-container" class="col-8">
            <QSkeleton type="rect" width="100%" height="350px" />
         </div>
         <QSeparator vertical></QSeparator>
         <!--         <SnapshotDoughnut-->
         <!--            v-if="secondaryVariableFilter.id < 0"-->
         <!--            ref="doughnut"-->
         <!--            class="col-4"-->
         <!--         ></SnapshotDoughnut>-->
         <!--         <div v-if="secondaryVariableFilter.id >= 0" id="chart">-->
         <div id="time-of-day-chart">
            <apexchart
               v-if="overallLoaded"
               :key="chartKey"
               type="radar"
               height="350"
               :options="chartOptions"
               :series="series"
            ></apexchart>
            <div
               v-else
               id="chart-skeleton-container"
               style="padding: 1em; width: 100%"
            >
               <QSkeleton type="rect" width="100%" height="350px" />
            </div>
         </div>
      </QCardSection>
   </QCard>
</template>
<script setup lang="ts">
import { computed, ComputedRef, ref, watch, onMounted, toRefs } from "vue"
import { QTable, QTableColumn } from "quasar"
import { useProjectStore } from "@stores/projectStore"
import { formatPercentTwoDecimals } from "./formatters"
import { VariantForRender } from "@/types/variable-snapshot"
import { VariableValue } from "@/types/variable"
import { ApexOptions } from "apexcharts"
import { ApexSeries } from "@/types/graphs"
import { getApexColor } from "@utils/chartColors"
import { useAnalyticsStore } from "@/stores/analyticsStore"
import { callWithLoading } from "@utils/callWithLoading"
import { useQuasar } from "quasar"

const HOURS = Array.from({ length: 24 }, (_, i) => i)

const analyticsStore = useAnalyticsStore()
const $q = useQuasar()
const { variableSnapshot, hourOfDay, selectedVariableId } =
   toRefs(analyticsStore)

const props = withDefaults(
   defineProps<{
      loading?: boolean
   }>(),
   {
      loading: false,
   },
)

const conversionRateOrServingRate = ref(false)
const enabled = ref(false) // Set to false to hide second variable picker due to API bug

// Set initial value for selectedVariableId if undefined
selectedVariableId.value = selectedVariableId.value || 0

const projectStore = useProjectStore()

// Computed property to get the currently selected variable's details
const activeVariable = computed(() => {
   const id = selectedVariableId.value
   if (!id) return null
   return projectStore.variables.find((x) => x.id === id)
})

// Initialize selectedVariableId if not set
const initializeSelectedVariable = () => {
   if (selectedVariableId.value === 0 && projectStore.variables.length > 0) {
      // Select the last variable by default (usually the most recently created)
      analyticsStore.setSelectedVariableId(
         projectStore.variables[projectStore.variables.length - 1].id,
      )
   }
}

const overallLoaded = computed(() => {
   return (
      !props.loading &&
      variableSnapshot.value &&
      projectStore.variables &&
      series.value
   )
})

const handleGetVariableExplorer = async () => {
   const id = selectedVariableId.value
   if (!id) return
   await callWithLoading(() => analyticsStore.fetchVariableExplorer(id), $q)
}

// Watch for variable selection changes
watch(
   () => selectedVariableId.value,
   async (newId) => {
      if (newId) await handleGetVariableExplorer()
   },
)

onMounted(async () => {
   initializeSelectedVariable()
   await handleGetVariableExplorer()
})

const getHourOfDay = computed(() => hourOfDay.value || [])

// Helper function to safely access store values with defaults
const getVariableSnapshot = computed(() => ({
   ids: variableSnapshot.value?.ids || [],
   variants: variableSnapshot.value?.variants || [],
}))

const chartKey = ref(0)
watch(
   [getHourOfDay, getVariableSnapshot, () => conversionRateOrServingRate.value],
   () => {
      chartKey.value++
   },
)

const onVariableClick = async (id: number) => {
   analyticsStore.setSelectedVariableId(id)
}

// Computed properties for display
const activeVariableFilter = computed(() => ({
   humanReadableName:
      activeVariable.value?.humanReadableName || "None Selected",
   id: selectedVariableId.value,
}))

const secondaryVariableFilter = ref({
   humanReadableName: "None",
   id: -1,
})

const onSecondaryVariableClick = async (id: number) => {
   secondaryVariableFilter.value = {
      humanReadableName:
         id === -1
            ? "None"
            : projectStore.variables.find((x) => x.id === id)
                 ?.humanReadableName || "None Selected",
      id,
   }
}

const variantColumn: QTableColumn = {
   name: "variant",
   label: "Variant",
   align: "center",
   field: (row: VariantForRender) => row.variant,
   sortable: true,
}

const sessionsColumn: QTableColumn = {
   name: "sessions",
   label: "Sessions",
   align: "left",
   field: (row: VariantForRender) => row.sessions,
   sortable: true,
}
const conversionsColumn: QTableColumn = {
   name: "conversions",
   label: "Conversions",
   align: "left",
   field: (row: VariantForRender) => row.conversions,
   sortable: true,
}

const conversionRateColumnLabel = computed(() => {
   if (conversionRateOrServingRate.value) {
      return `Conversion Rate`
   } else {
      return `Serving Rate`
   }
})

const conversionRateColumn: QTableColumn = {
   name: "conversion-rate",
   label: "Conversion Rate",
   align: "left",
   field: (row: VariantForRender) => row.conversionRate,
   sortable: true,
   format: (v: number) => formatPercentTwoDecimals(v),
}

const trafficDistributionColumn: QTableColumn = {
   name: "traffic-distribution",
   label: "Traffic %",
   align: "left",
   field: (row: VariantForRender) => row.trafficPct,
   sortable: true,
   format: (v: number) => formatPercentTwoDecimals(v),
}

const columns = computed(() => {
   const variables = getVariableSnapshot.value.ids
   return [
      variantColumn,
      sessionsColumn,
      conversionsColumn,
      trafficDistributionColumn,
      conversionRateColumn,
      ...variables.map((vId) => {
         return <QTableColumn>{
            name: projectStore.getName(vId),
            label: projectStore.getName(vId),
            align: "left",
            field: (row: VariantForRender) => {
               return row.variables.find((x) => x.variableId == vId)?.value
            },
            sortable: true,
            style: "max-width: 400px; text-wrap: wrap;",
         }
      }),
   ]
})

const defaultConversionRate = computed(() => {
   // Fallback to variant with ID=0
   const defaultVariant = getVariableSnapshot.value.variants?.find(
      (v) => v.variant === "0",
   )
   return defaultVariant?.conversion_rate || 0
})

const rows = computed(() => {
   const variantStats = getVariableSnapshot.value.variants
   if (!variantStats) {
      return []
   }
   const agg: VariantForRender[] = []
   variantStats.forEach((variantStat, i) => {
      const combinationNumber = variantStat.variant
      const variables = combinationNumber.split(",")
      const vars = variables.map((variable) => {
         const spl = variable.split(":")
         const id: number = +spl[0]
         const index: number = +spl[1]
         return <VariableValue>{
            variableId: id,
            humanReadableName: projectStore.getName(id),
            value: projectStore.getValue(id, index),
         }
      })

      const newComboForRender = <VariantForRender>{
         variant: combinationNumber,
         conversionRate: variantStat.conversion_rate,
         trafficPct: variantStat.traffic_pct,
         variantColor: getApexColor(i),
         statSig: {
            lb: variantStat.cr_lb,
            ub: variantStat.cr_ub,
            defaultConversionRate: defaultConversionRate.value,
         },
         conversions: variantStat.conversions,
         sessions: variantStat.sessions,
         variables: vars,
      }

      agg.push(newComboForRender)
   })
   return agg
})

const chartOptions: ComputedRef<ApexOptions> = computed(() => {
   return {
      chart: {
         height: 450,
         type: "radar",
      },
      dataLabels: {
         enabled: false,
      },
      markers: {
         size: 0,
      },
      xaxis: {
         categories: getHourOfDay.value?.[0]?.hour_of_day || HOURS,
      },
      yaxis: {
         labels: {
            formatter: (value: number) => {
               return formatPercentTwoDecimals(value)
            },
         },
      },
      title: {
         text: conversionRateColumnLabel.value + " by Hour of Day (UTC)",
      },
   }
})

const series: ComputedRef<ApexSeries[]> = computed(() => {
   return (
      getHourOfDay.value?.map((hod) => {
         let color = undefined
         const match = rows.value.find((row) => row.variant === hod.variant)
         if (match) {
            color = match.variantColor
         }
         return {
            name: hod.variant,
            data: conversionRateOrServingRate.value
               ? hod.conversion_rate
               : hod.traffic_pct,
            color,
         } as ApexSeries
      }) || []
   )
})
</script>
<style lang="scss" scoped>
#chart-skeleton-container {
   padding: 1em;
}
#combinations-table-container {
   margin-top: 20px;
   margin-bottom: 20px;
   width: 100%;
   .q-td {
      text-align: left;
   }
}
.toggles-filters-container {
   display: flex;
   justify-content: flex-start;
   margin-bottom: 2em;
   // border: 1px solid #e0e0e0;
   // border-radius: 5px;
}
#no-data {
   font-size: 1.25em;
   color: $primary;
   font-weight: bold;
   margin-left: 10px;
}
#no-data-container {
   min-height: 30vh;
}
#selector-container {
   display: flex;
   flex-direction: column;
   gap: 10px;
   padding: 20px;
}
.indicator {
   display: inline-block;
   width: 10px;
   height: 10px;
   border-radius: 50%;
   margin-right: 5px;
}
#time-of-day-chart {
   width: 100%;
}
</style>
